
import { useState, useEffect, useRef } from 'react';
import { Helix2000Character } from '../../shared/CharacterTypes';
import { gameLogger } from '../../utils/GameLogger';
import { GameBootstrap } from '../services/GameBootstrap';
import type { GameScene } from '../scenes/GameScene';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { cleanMainGameAddressBar } from '../../config/boot/mainGamePlayUrl';

interface UseGameBootOptions {
  player: Helix2000Character | null;
  authToken: string | null;
  isAuthenticated: boolean;
  initialScene: string;
}

export function useGameBoot(options: UseGameBootOptions) {
  const { player, authToken, isAuthenticated, initialScene } = options;

  const [gameInstance, setGameInstance] = useState<any | null>(null);
  const [gameWorld, setGameWorld] = useState<GameScene | null>(null);
  const gameInstanceRef = useRef<any | null>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);

  // Initialize the game host
  useEffect(() => {
    if (isAuthenticated && gameContainerRef.current && !gameInstanceRef.current && player) {
      gameLogger.info('game', '🎮 [useGameBoot] Calling GameBootstrap.init()...', {
        playerName: player?.name,
      });

      const game = GameBootstrap.init({
        containerElement: gameContainerRef.current,
        player,
        authToken,
        initialScene,
        onGameCreated: (g) => {
          gameInstanceRef.current = g;
          setGameInstance(g);
        },
        onWorldCreated: (world) => setGameWorld(world)
      });

      if (!game) {
        gameLogger.error('game', '❌ [useGameBoot] GameBootstrap.init() returned null!');
      }
    }
  }, [isAuthenticated, initialScene, player, authToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameInstanceRef.current) {
        gameLogger.info('game', '🧹 [useGameBoot] Unmount cleanup: destroying game host');
        gameInstanceRef.current.destroy();
        gameInstanceRef.current = null;
        (window as any).__GAME_HOST__ = null;
        (window as any).__GAME_BOOT__ = null;
        setGameInstance(null);
        setGameWorld(null);
      }
    };
  }, []);

  // Handle GameScene ready events
  useEffect(() => {
    const handleSceneReady = (scene: GameScene) => {
      gameLogger.info('game', '🌏 GameScene Ready Event received, updating world reference');
      setGameWorld(scene);
      // Boot has consumed the URL: on helix2000.com show a clean `/play`
      // (late readers use bootUrl.ts; a reload restores the boot URL).
      try {
        if (cleanMainGameAddressBar()) gameLogger.info('boot', 'Address bar cleaned to /play (main game domain)');
      } catch { /* best-effort — a long address bar is cosmetic */ }
    };

    // GAME_SCENE_READY carries a game-side GameScene (shared can't name it) → dynamic.
    const unsubSceneReady = EventBus.getInstance().onDynamic(EVENTS.GAME_SCENE_READY, handleSceneReady);

    return () => {
      unsubSceneReady();
    };
  }, []);

  const destroyGame = () => {
    if (gameInstanceRef.current) {
      gameInstanceRef.current.destroy();
      gameInstanceRef.current = null;
      (window as any).__GAME_HOST__ = null;
      (window as any).__GAME_BOOT__ = null;
      setGameInstance(null);
      setGameWorld(null);
    }
  };

  return {
    gameInstance,
    gameWorld,
    gameContainerRef,
    setGameWorld,
    destroyGame
  };
}
